/* eslint-disable react/no-unescaped-entities */
/**
 * Public changelog page — pulls all approved launch posts from
 * db.viralpost_queue and renders them as a customer-facing "What's new"
 * feed. Every time the team clicks "Launch post" in ViralPost, that
 * shipping event becomes a public changelog entry.
 */
import { useEffect, useState } from "react";
import { Sparkles, Calendar, ArrowRight, Facebook, Linkedin, Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/layout/PublicHeader";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function Changelog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/viralpost/changelog`)
      .then((r) => r.json())
      .then((d) => setItems(d.items || []))
      .catch(() => { /* no-op */ })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "What's new at Central Rentals — Changelog";
    const setMeta = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("meta"); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };
    setMeta('meta[name="description"]', {
      name: "description",
      content: "Every shipping event from Central Rentals — features, fixes, and infrastructure updates for Canadian landlords.",
    });
    setMeta('link[rel="canonical"]', { rel: "canonical", href: "https://centralrentals.ca/changelog" });
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="changelog-page">
      <PublicHeader light={true}/>
      <section className="max-w-3xl mx-auto px-5 md:px-8 pt-12 pb-8">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-3">Changelog</div>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05] text-slate-900">
          What's new at <span className="text-emerald-700">Central Rentals</span>.
        </h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl leading-relaxed">
          Every feature, fix, and infrastructure update we ship — in order, oldest at the bottom. Want a monthly digest? <Link to="/#help-centre" className="text-emerald-700 hover:underline">Join the newsletter</Link>.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-16">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500">
            No published launches yet. Check back soon.
          </div>
        )}
        <ol className="relative border-l-2 border-slate-200 pl-6 space-y-10" data-testid="changelog-list">
          {items.map((it, i) => (
            <li key={it.id || i} data-testid={`changelog-item-${i}`} className="relative">
              <span className="absolute -left-[34px] top-1 w-4 h-4 rounded-full bg-emerald-600 border-4 border-white shadow"/>
              <div className="text-xs text-slate-500 uppercase tracking-wider font-bold flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5"/> {(it.created_at || "").slice(0, 10)}
              </div>
              <h2 className="mt-2 font-display text-2xl font-bold text-slate-900 leading-tight">
                {it.launch_note || it.headline || "New release"}
              </h2>
              {it.image_data_uri && (
                <img src={it.image_data_uri} alt="" className="mt-4 w-full max-w-xl rounded-none border border-slate-200"/>
              )}
              {it.platforms?.linkedin && (
                <p className="mt-4 text-base text-slate-700 leading-relaxed whitespace-pre-line">
                  {it.platforms.linkedin.split("\n").slice(0, 3).join("\n")}
                </p>
              )}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://centralrentals.ca/changelog#${it.id}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-blue-700"><Facebook className="w-3 h-3"/>Share</a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://centralrentals.ca/changelog#${it.id}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-sky-700"><Linkedin className="w-3 h-3"/>Share</a>
                <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(it.launch_note || "")}&url=${encodeURIComponent(`https://centralrentals.ca/changelog#${it.id}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900"><Twitter className="w-3 h-3"/>Share</a>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-12 text-center">
          <Link to="/get-started" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            Try Central Rentals free <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </section>
    </div>
  );
}
